import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { VoteDataProvider } from './context/VoteDataContext';
import { PlayerComparisonProvider } from './context/PlayerComparisonContext';
import { HandicapProvider } from './context/HandicapContext';
import { trackPageView } from './utils/analytics';
import { DEFAULT_LANDING_ROUTE } from './routes/destinations';
import LandingPage from './components/LandingPage/LandingPage';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGate from './components/RoleGate';
import ComingSoonModal from './components/ComingSoonModal';
import Dashboard from './components/Dashboard/Dashboard';
import VoteProgression from './components/VoteProgression/VoteProgression';
import VoteSummary from './components/VoteSummary/VoteSummary';
import PlayerComparison from './components/PlayerComparison/PlayerComparison';
import BYOBrownlow from './components/BYOBrownlow/BYOBrownlow';
import GameVotes from './components/GameVotes/GameVotes';
import TeamVotes from './components/TeamVotes/TeamVotes';
import TermsOfService from './components/LandingPage/TermsOfService';
import PrivacyPolicy from './components/LandingPage/PrivacyPolicy';
// Removed SignupCancelled page; cancel redirects to landing

// Component to track route changes for Google Analytics
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
};

// Home route — signed-in users are sent straight into the app; everyone else
// gets the landing page (which hosts the login/register modals). Waits for
// the token rehydrate to finish so a signed-in refresh doesn't flash the
// marketing page before bouncing.
const HomeRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to={DEFAULT_LANDING_ROUTE} replace />;
  return <LandingPage />;
};

// Component to handle logout redirect
const LogoutHandler = () => {
  const { logout, isAuthenticated } = useAuth();

  useEffect(() => {
    logout();
  }, [logout]);

  // Don't navigate until the session is actually cleared, otherwise HomeRoute
  // can see a still-authenticated user and bounce straight back into the app.
  if (isAuthenticated) return null;
  return <Navigate to="/" replace />;
};

// Wrapper for protected routes with mobile menu state. Accounts without one of
// the ACCESS_ROLES (config/access.js) still see the page shell, but locked
// behind the coming-soon overlay; delete the RoleGate wrapper at GA.
const ProtectedRouteWrapper = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <ProtectedRoute>
      <RoleGate fallback={<ComingSoonModal />}>
        {React.cloneElement(children, {
          isMobileMenuOpen,
          setIsMobileMenuOpen
        })}
      </RoleGate>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <VoteDataProvider>
          <PlayerComparisonProvider>
            <HandicapProvider>
            <Router>
          <RouteTracker />
          <div className="App">
          <Routes>
            {/* Home / Landing page — login is handled via the built-in modal */}
            <Route path="/" element={<HomeRoute />} />
            {/* Legacy /login path redirects to home */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            {/* Routes for free application - no payment integration needed */}
            
            {/* Public Dashboard Route */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRouteWrapper>
                  <Dashboard />
                </ProtectedRouteWrapper>
              } 
            />

            {/* Public Vote Progression Route */}
            <Route 
              path="/vote-progression" 
              element={
                <ProtectedRouteWrapper>
                  <VoteProgression />
                </ProtectedRouteWrapper>
              } 
            />

            {/* Public Team Votes Route */}
            <Route 
              path="/team-votes" 
              element={
                <ProtectedRouteWrapper>
                  <TeamVotes />
                </ProtectedRouteWrapper>
              } 
            />
            
            {/* Public Vote Summary Route */}
            <Route 
              path="/vote-summary" 
              element={
                <ProtectedRouteWrapper>
                  <VoteSummary />
                </ProtectedRouteWrapper>
              } 
            />
            
            {/* Public Player Comparison Route */}
            <Route 
              path="/player-comparison" 
              element={
                <ProtectedRouteWrapper>
                  <PlayerComparison />
                </ProtectedRouteWrapper>
              } 
            />
            
            {/* Public BYOBrownlow Tool Route */}
            <Route 
              path="/byo-brownlow" 
              element={
                <ProtectedRouteWrapper>
                  <BYOBrownlow />
                </ProtectedRouteWrapper>
              } 
            />
            
            {/* Public Game Votes Route */}
            <Route 
              path="/game-votes" 
              element={
                <ProtectedRouteWrapper>
                  <GameVotes />
                </ProtectedRouteWrapper>
              } 
            />
      
            {/* Logout Route */}
            <Route path="/logout" element={<LogoutHandler />} />
            {/* Terms of Service */}
            <Route path="/terms" element={<TermsOfService />} />
            {/* Privacy Policy */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            
            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </div>
            </Router>
            </HandicapProvider>
          </PlayerComparisonProvider>
        </VoteDataProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;